import React from 'react';
import { Field, FieldProps, Form, Formik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Button, CentBasedInputCurrency, FieldWrapper, InputIconWrapper, Modal, ModalHeader, ModalTitle, Text } from 'components';
import { theme, toRem } from 'design';
import { ButtonContainer, NumberInput } from 'screens/SiteSettings/TipCalculator/shared/styles';
import { IEmployeeSiteWithholdings, ISiteWithholdings } from 'types';
import { centsToDollars } from 'utils';

const validatePercentInput = (value: string) => {
  // Allow whole numbers up to 3 digits
  const pattern = /^\d{1,3}$/i;
  return value === '' || pattern.test(value);
};

const SettingsContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  padding: ${toRem(theme.space.xl)};
  border-radius: ${toRem(11)};
  background-color: ${theme.color.neutral[50]};
  width: 100%;

  .optional-field {
    label > span {
      color: ${theme.color.neutral[400]};
      text-transform: capitalize;
    }
  }
`;

interface IWithholdingsFormValues {
  minimum: number;
  rate: number;
  cap: number;
}

interface IEditWithholdingsModalProps {
  hideModal: () => void;
  onSave: (withholdings: ISiteWithholdings | IEmployeeSiteWithholdings) => void;
  withholdings: ISiteWithholdings | IEmployeeSiteWithholdings;
  isProcessing: boolean;
  modalTitle?: string;
  modalSubtitle?: string;
}

const EditWithholdingsModal = ({ hideModal, onSave, withholdings, modalTitle, modalSubtitle, isProcessing }: IEditWithholdingsModalProps) => {
  const validationSchema = Yup.object().shape({
    minimum: Yup.number()
      .default(0)
      .required('Minimum is required')
      .min(0, 'Minimum must be at least $0')
      .test({
        name: 'minimum-whole-dollar-amount',
        test: (value, ctx) => centsToDollars(value) % 1 === 0 || ctx.createError({ message: 'The withholding minimum must be a whole dollar amount.' }),
      }),
    rate: Yup.number().default(0).required().min(0, 'Must be at least 0%').max(99, 'The withholding rate cannot exceed 99%'),
    cap: Yup.number()
      .default(undefined)
      .test({
        name: 'cap-whole-dollar-amount',
        test: (value, ctx) => value === undefined || centsToDollars(value) % 1 === 0 || ctx.createError({ message: 'The withholding cap must be a whole dollar amount.' }),
      })
      .test({
        name: 'cap-greater-than-minimum',
        test: (value, ctx) => value === undefined || value > ctx.parent.minimum || ctx.createError({ message: 'The withholding cap must be larger than withholding minimum.' }),
      }),
  });

  const handleSubmit = (values: IWithholdingsFormValues) => {
    const updatedWithholdings = { ...withholdings, ...values, ...(!values.cap && { cap: undefined }) };
    onSave(updatedWithholdings);
  };

  return (
    <Modal maxWidth={600} onClose={hideModal} isActive closeOnOutSideClick={false} dataCy="edit-withholdings-modal">
      <ModalHeader style={{ gap: toRem(theme.space.md) }} data-cy="edit-withholdings-modal-header">
        <ModalTitle>{modalTitle ?? 'Default Withholding'}</ModalTitle>
        <Text size="small">{modalSubtitle ?? 'Default withholding to be applied to all site employees'}</Text>
      </ModalHeader>
      <Formik initialValues={{ minimum: withholdings.minimum ?? 0, rate: withholdings.rate ?? 0, cap: withholdings.cap }} onSubmit={handleSubmit} validationSchema={validationSchema} enableReinitialize>
        {({ setFieldValue, isValid, dirty }) => (
          <Form>
            <SettingsContainer data-cy="edit-withholdings-modal-settings-container">
              <Field name="minimum">
                {({ field, meta }: FieldProps) => (
                  <FieldWrapper label="Withholding Minimum" hasError={meta.error && meta.touched} message={meta.error && meta.touched ? meta.error : ''}>
                    <CentBasedInputCurrency
                      {...field}
                      value={field.value?.toString() || ''}
                      onChange={(value) => setFieldValue('minimum', parseInt(value, 10))}
                      hasError={meta.error && meta.touched}
                      allowZero
                      data-cy="withholdings-minimum-input"
                    />
                  </FieldWrapper>
                )}
              </Field>
              <Field name="rate">
                {({ field, meta }: FieldProps) => (
                  <FieldWrapper label="Withholding Rate" hasError={meta.error && meta.touched} message={meta.error && meta.touched ? meta.error : ''}>
                    <InputIconWrapper icon="Percentage" iconPosition="right">
                      <NumberInput
                        inputMode="decimal"
                        {...field}
                        onChange={(e) => {
                          if (!validatePercentInput(e.target.value)) return;
                          setFieldValue('rate', Number(e.target.value) || 0);
                        }}
                        value={field.value || 0}
                        $error={meta.error && meta.touched}
                        data-cy="withholdings-rate-input"
                      />
                    </InputIconWrapper>
                  </FieldWrapper>
                )}
              </Field>
              <Field name="cap">
                {({ field, meta }: FieldProps) => (
                  <FieldWrapper className="optional-field" label="Withholding Cap" optional hasError={!!meta.error} message={meta.error || ''}>
                    <CentBasedInputCurrency
                      {...field}
                      value={field.value?.toString() || ''}
                      onChange={(value) => {
                        const parsedValue = parseInt(value, 10);
                        setFieldValue('cap', parsedValue === 0 ? undefined : parsedValue);
                      }}
                      hasError={!!meta.error}
                      data-cy="withholdings-cap-input"
                    />
                  </FieldWrapper>
                )}
              </Field>
            </SettingsContainer>
            <ButtonContainer isModal>
              <Button appearance="white" label="Cancel" onClick={hideModal} data-cy="edit-withholdings-modal-cancel-button" />
              <Button type="submit" label="Save" isProcessing={isProcessing} disabled={!isValid || !dirty} data-cy="edit-withholdings-modal-save-button" />
            </ButtonContainer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default EditWithholdingsModal;
